import React, {useMemo, useState} from 'react';
import styled from 'styled-components';
import {useDocumentTitle} from '@bootcamp/shared/src/util/hooks';
import {useHistory} from 'react-router-dom';

import {Selector} from '../../pages/ScoreReport/components/Filter';

import {useBootcampConfigContext} from '@bootcamp/web/src/contexts/BootcampConfig';
import {Element as FAQElement} from '@bootcamp/web/src/bootcamps/components/WebflowFAQs';
import Breadcrumbs from '../../../components/NavBar/components/Breadcrumbs';

import {
  PageHeader,
  Wrapper,
  ElementWrapper,
  StyledCard,
  LoadingWrapper
} from '@bootcamp/web/src/bootcamps/pages/ScoreReport';

import Details from '@bootcamp/web/src/bootcamps/pages/ScoreReport/components/Details';
import Score from '@bootcamp/web/src/bootcamps/pages/ScoreReport/components/Score';
import Subject from '@bootcamp/web/src/bootcamps/pages/ScoreReport/components/Subject';
import Question from '@bootcamp/web/src/bootcamps/pages/ScoreReport/components/Question';
import Time from '@bootcamp/web/src/bootcamps/pages/ScoreReport/components/Time';
import ScoreDistributionChart from '@bootcamp/web/src/components/Charts/ScoreDistribution';

import {Heading, Title, TitleWithFilter, SubTitle} from '@bootcamp/web/src/bootcamps/components/Performance/shared';

import {ConfigProvider, useConfigState} from '@bootcamp/web/src/bootcamps/pages/ScoreReport/context';

import {findInConfigById} from '@bootcamp/shared/src/util';
import { useUserDataContext } from '../../../contexts/UserData';

function getTestTitle(config, testId) {
  try {
    const configHit = findInConfigById(config, testId);
    const fltConfigHit = [config?.studyTools?.[0]?.content?.find(({ id }) => id === testId)];

    const test = (configHit || fltConfigHit)?.[0];

    return test?.name;
  } catch (error) {
    console.log('error getting test name', error);
    return 'Practice Test'
  }
}

const ChartCard = styled(StyledCard)`
  padding: 24px;

  ${({theme}) => theme.mediaQueries.tablet} {
    padding: 16px;
  }
`;

const optionsByBootcamp = {
  dat: [
    {value:"Biology", text: 'Biology'},
    {value:"General Chemistry", text: 'General Chemistry'},
    {value:"Organic Chemistry", text: 'Organic Chemistry'},
    {value:"Perceptual Ability", text: 'Perceptual Ability'},
    {value:"Reading Comprehension", text: 'Reading Comprehension'},
    {value:"Quantitative Reasoning", text: 'Quantitative Reasoning'},
  ],
  oat: [
    {value:"Biology", text: 'Biology'},
    {value:"General Chemistry", text: 'General Chemistry'},
    {value:"Organic Chemistry", text: 'Organic Chemistry'},
    {value:"Reading Comprehension", text: 'Reading Comprehension'},
    {value:"Quantitative Reasoning", text: 'Quantitative Reasoning'},
    {value:"Physics", text: 'Physics'},
  ],
};

const DistributionChart = ({testTitle, isFullLength}) => {
  const [subject, setSubject] = useState('Biology');
  const {scoring, bootcamp} = useConfigState();
  const testNumber = testTitle.match(/#(.*)/)[1];
  const subjectTestTitle = `${subject} Test #${testNumber}`;

  const title = isFullLength ? subjectTestTitle : testTitle;

  const score = isFullLength
    ? scoring?.subjectBreakdown?.[subject]?.score
    : scoring?.overview?.score;

  return (
    <ElementWrapper>
      <Heading>
        <TitleWithFilter>
          <span>
            {isFullLength ? 'Subject Comparison' : 'Comparison'}
          </span>
          {isFullLength &&
            <Selector bootcamp={bootcamp} setSelection={setSubject} options={optionsByBootcamp[bootcamp]}/>
          }
        </TitleWithFilter>
        <SubTitle>See how you compare to other students based on the number of questions you answered correctly. </SubTitle>
      </Heading>
      <ChartCard>
        <ScoreDistributionChart name={title} score={score}/>
      </ChartCard>
    </ElementWrapper>
  );
}

const faqItems = [
  {title: "How can I improve my score?", text: "The best way to improve your score is to make sure you are reading through each explanation thoroughly - even for the questions you got correct. Review the Subject Breakdown table above and focus on subjects marked with a red icon - that's where you can improve the most!"},
];

const TestReview = ({match}) => {
  const history = useHistory();
  const {config} = useBootcampConfigContext();
  const {bootcamp} = useUserDataContext();
  const {params: {id}} = match;

  const testTitle = useMemo(() => getTestTitle(config, id), [id]);
  const isFullLength = testTitle?.match('Full Length');

  useDocumentTitle(`${config.meta.siteTitle} | Test Review`);
  
  if (window.location.pathname.includes('sample-test')) {
    return (
      <ConfigProvider>
        <LoadingWrapper>
          <ElementWrapper>
            <Breadcrumbs history={history} lockAtDepth={3}/>
            <PageHeader>DAT Sample Test</PageHeader>
          </ElementWrapper>
          <Wrapper>
            <ElementWrapper>
              <StyledCard>
                <Details/>
                <Score
                  testTitle={'Full Length Test #1'}
                  hideChart
                  hideBlurb
                />
              </StyledCard>
            </ElementWrapper>
            <Subject isFullLengthTest />
            <Question filterOptions={optionsByBootcamp[bootcamp]} hideExpand />
            <ElementWrapper>
              <Heading>
                <Title>FAQs</Title>
              </Heading>
              {faqItems.map(({title, text}) => <FAQElement key={title} title={title} text={text} />)}
            </ElementWrapper>
          </Wrapper>
        </LoadingWrapper>
      </ConfigProvider>
    )
  }


  return (
    <ConfigProvider>
      <LoadingWrapper>
        <ElementWrapper>
          <Breadcrumbs history={history} lockAtDepth={isFullLength ? 3 : 4}/>
          <PageHeader>{testTitle}</PageHeader>
        </ElementWrapper>
        <Wrapper>
          <ElementWrapper>
            <StyledCard>
              <Details/>
              <Score
                testTitle={testTitle}
                isFullLengthTest={isFullLength}
                />
            </StyledCard>
          </ElementWrapper>
          <DistributionChart testTitle={testTitle} isFullLength={isFullLength}/>
          <Subject isFullLengthTest={isFullLength}/>
          <Time topicOnly={!isFullLength}/>
          <Question filterOptions={isFullLength ? optionsByBootcamp[bootcamp] : []} hideExpand/>
          <ElementWrapper>
            <Heading>
              <Title>FAQs</Title>
            </Heading>
            {faqItems.map(({ title, text }) => <FAQElement key={title} title={title} text={text} />)}
          </ElementWrapper>
        </Wrapper>
      </LoadingWrapper>
  </ConfigProvider>
  );
};

TestReview.defaultProps = {};
TestReview.propTypes = {};

export default TestReview;